import React from 'react';
import { motion } from 'framer-motion';
import ScrollHeader from '../components/ScrollHeader';
import ImageModal from '../components/ImageModal';

const Visualisation: React.FC = () => {
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);

  return (
    <div className="max-w-4xl mx-auto">
      <ScrollHeader title="Visualisation" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Visualize your infrastructure architecture, add components interactively, preview deployment costs, and deploy with confidence.
          </p>

          <div className="bg-primary-500/10 dark:bg-primary-500/20 border-l-4 border-primary-500 p-6 rounded-r-lg mb-8">
            <h3 className="text-primary-600 dark:text-primary-400 mt-0 mb-2">💡 Key Concept</h3>
            <p className="mb-0">
              The Visualisation tool transforms your infrastructure code into an interactive visual diagram, 
              making it easy to understand complex architectures, add new components, preview costs, and deploy 
              your infrastructure to the cloud.
            </p>
          </div>

          <h2>Overview</h2>
          <p>
            The Visualisation tool is accessed from the Code Editor via the eye button (👁️). It provides a 
            visual representation of your infrastructure, showing all components and their relationships in an 
            easy-to-understand diagram format.
          </p>

          <div 
            className="my-8 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            onClick={() => setSelectedImage('/screenshots/visualisation/visualisation_main.png')}
          >
            <img 
              src="/screenshots/visualisation/visualisation_main.png" 
              alt="Infrastructure Visualisation" 
              className="w-full"
            />
          </div>

          <h2>Key Features</h2>

          <h3>Interactive Infrastructure Diagram</h3>
          <p>
            The visualisation displays your infrastructure as an interactive node-based diagram:
          </p>
          <ul>
            <li><strong>Nodes:</strong> Each infrastructure component is represented as a node</li>
            <li><strong>Connections:</strong> Lines show relationships and dependencies between components</li>
            <li><strong>Color Coding:</strong> Different colors indicate different resource types</li>
            <li><strong>Zoom & Pan:</strong> Navigate large infrastructures easily</li>
          </ul>

          <h3>Add New Components</h3>
          <p>
            You can add new infrastructure components directly from the visual interface:
          </p>
          <ul>
            <li>Browse available resource types from the options panel</li>
            <li>Drag and drop new nodes onto the canvas</li>
            <li>Connect components to define relationships</li>
            <li>Configure properties through the visual interface</li>
          </ul>

          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 rounded-r-lg mb-6">
            <h4 className="text-blue-800 dark:text-blue-400 mt-0 mb-2">🎨 Visual Editing</h4>
            <p className="mb-0 text-blue-700 dark:text-blue-300">
              Adding nodes visually automatically updates the underlying infrastructure code. 
              You can switch back to the Code Editor to see the changes reflected in the code.
            </p>
          </div>

          <h3>Deployment Preview</h3>
          <p>
            Before deploying, you can preview the deployment plan to understand what will be created, modified, or destroyed:
          </p>

          <div 
            className="my-8 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            onClick={() => setSelectedImage('/screenshots/visualisation/plan_preview.png')}
          >
            <img 
              src="/screenshots/visualisation/plan_preview.png" 
              alt="Deployment Plan Preview" 
              className="w-full"
            />
          </div>

          <h4>Plan Preview Shows:</h4>
          <ul>
            <li><strong>Resources to Create:</strong> New infrastructure components</li>
            <li><strong>Resources to Modify:</strong> Existing components that will be updated</li>
            <li><strong>Resources to Destroy:</strong> Components that will be removed</li>
            <li><strong>Cost Estimates:</strong> Projected monthly costs for the infrastructure</li>
            <li><strong>Deployment Time:</strong> Estimated time to complete the deployment</li>
          </ul>

          <h3>Cost Estimation</h3>
          <p>
            One of the most powerful features is the cost preview functionality:
          </p>

          <div 
            className="my-8 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            onClick={() => setSelectedImage('/screenshots/visualisation/deploy_preview.png')}
          >
            <img 
              src="/screenshots/visualisation/deploy_preview.png" 
              alt="Deployment Cost Preview" 
              className="w-full"
            />
          </div>

          <ul>
            <li><strong>Per-Resource Costs:</strong> See the cost of each individual component</li>
            <li><strong>Total Monthly Cost:</strong> Aggregate cost for the entire infrastructure</li>
            <li><strong>Cost Breakdown:</strong> Detailed breakdown by service type</li>
            <li><strong>Cost Optimization Tips:</strong> Suggestions to reduce costs</li>
          </ul>

          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-6 rounded-r-lg mb-6">
            <h4 className="text-green-800 dark:text-green-400 mt-0 mb-2">💰 Cost Awareness</h4>
            <p className="mb-0 text-green-700 dark:text-green-300">
              Always review the cost preview before deploying. This helps you avoid unexpected charges 
              and ensures your infrastructure stays within budget. You can adjust resource sizes and 
              configurations to optimize costs.
            </p>
          </div>

          <h2>Deployment Process</h2>

          <h3>Prerequisites</h3>
          <p>
            Before you can deploy infrastructure, you must:
          </p>
          <ol>
            <li>
              <strong>Set up Cloud Integration:</strong> Configure your cloud provider credentials in the Integrations page
            </li>
            <li>
              <strong>Review the Plan:</strong> Carefully examine the deployment plan and cost estimates
            </li>
            <li>
              <strong>Verify Permissions:</strong> Ensure your cloud credentials have the necessary permissions
            </li>
          </ol>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-6 rounded-r-lg mb-6">
            <h4 className="text-yellow-800 dark:text-yellow-400 mt-0 mb-2">⚠️ Integration Required</h4>
            <p className="mb-0 text-yellow-700 dark:text-yellow-300">
              You cannot deploy infrastructure without first setting up a cloud provider integration. 
              Navigate to the Integrations page to configure your AWS, Azure, or GCP credentials.
            </p>
          </div>

          <h3>Deployment Steps</h3>
          <ol>
            <li>
              <strong>Click Deploy Button:</strong> Initiate the deployment process
            </li>
            <li>
              <strong>Review Plan:</strong> The system shows you what will be created/modified/destroyed
            </li>
            <li>
              <strong>Review Costs:</strong> Check the estimated monthly costs
            </li>
            <li>
              <strong>Confirm Deployment:</strong> Approve the deployment to proceed
            </li>
            <li>
              <strong>Monitor Progress:</strong> Watch as Ana deploys your infrastructure using Terraform
            </li>
            <li>
              <strong>Verify Success:</strong> Check the deployment status and any output values
            </li>
          </ol>

          <h3>How Deployment Works</h3>
          <p>
            Ana uses Terraform to deploy your infrastructure:
          </p>
          <ul>
            <li>Converts your infrastructure code to Terraform format</li>
            <li>Authenticates with your cloud provider using configured credentials</li>
            <li>Executes <code>terraform plan</code> to preview changes</li>
            <li>Executes <code>terraform apply</code> to create/update resources</li>
            <li>Provides real-time feedback on deployment progress</li>
            <li>Stores state information for future updates</li>
          </ul>

          <h2>Visual Editing Capabilities</h2>

          <h3>Adding Nodes</h3>
          <p>
            The visualisation tool provides a library of infrastructure components you can add:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2 text-base">Compute</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-400 mb-0 space-y-1">
                <li>EC2 Instances</li>
                <li>Lambda Functions</li>
                <li>ECS/EKS Clusters</li>
                <li>Auto Scaling Groups</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2 text-base">Storage</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-400 mb-0 space-y-1">
                <li>S3 Buckets</li>
                <li>EBS Volumes</li>
                <li>RDS Databases</li>
                <li>DynamoDB Tables</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2 text-base">Networking</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-400 mb-0 space-y-1">
                <li>VPCs</li>
                <li>Subnets</li>
                <li>Load Balancers</li>
                <li>Security Groups</li>
              </ul>
            </div>
          </div>

          <h3>Modifying Nodes</h3>
          <p>
            Click on any node to:
          </p>
          <ul>
            <li>View and edit its properties</li>
            <li>See its connections to other components</li>
            <li>Delete the component</li>
            <li>Duplicate the component</li>
          </ul>

          <h2>Best Practices</h2>

          <h3>Review Before Deploying</h3>
          <p>
            Always review the deployment plan and cost estimates before deploying:
          </p>
          <ul>
            <li>Verify all resources are correctly configured</li>
            <li>Check that costs are within your budget</li>
            <li>Ensure security groups and IAM policies are appropriate</li>
            <li>Confirm the deployment won't affect existing resources</li>
          </ul>

          <h3>Start Small</h3>
          <p>
            For complex infrastructures, consider deploying in stages:
          </p>
          <ol>
            <li>Deploy core networking components first</li>
            <li>Add compute resources</li>
            <li>Deploy databases and storage</li>
            <li>Configure monitoring and logging</li>
            <li>Set up CI/CD pipelines</li>
          </ol>

          <h3>Use the Visual Editor</h3>
          <p>
            The visual editor is particularly useful for:
          </p>
          <ul>
            <li>Understanding complex architectures at a glance</li>
            <li>Identifying missing components or connections</li>
            <li>Explaining infrastructure to team members</li>
            <li>Quickly adding standard components</li>
          </ul>

          <h2>Troubleshooting</h2>

          <h3>Deployment Fails</h3>
          <p>
            If deployment fails, check:
          </p>
          <ul>
            <li>Cloud provider credentials are valid and have necessary permissions</li>
            <li>Resource quotas and limits in your cloud account</li>
            <li>Region availability for requested resources</li>
            <li>Naming conflicts with existing resources</li>
          </ul>

          <h3>Cost Estimates Unavailable</h3>
          <p>
            If cost estimates don't appear:
          </p>
          <ul>
            <li>Ensure cloud integration is properly configured</li>
            <li>Verify the region is supported for cost estimation</li>
            <li>Check that resource types are recognized</li>
          </ul>

          <h2>Next Steps</h2>
          <p>
            After visualizing and deploying your infrastructure:
          </p>
          <ul>
            <li>
              <strong>Monitor Deployment:</strong> Track the deployment progress in real-time
            </li>
            <li>
              <strong>Verify Resources:</strong> Check your cloud provider console to confirm resources were created
            </li>
            <li>
              <strong>Use Cloud Analyzer:</strong> Get recommendations for optimizing your deployed infrastructure
            </li>
            <li>
              <strong>Update as Needed:</strong> Make changes in Chat or Code Editor and redeploy
            </li>
          </ul>

          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-6 rounded-r-lg mt-8">
            <h3 className="text-green-800 dark:text-green-400 mt-0 mb-2">✨ Pro Tip</h3>
            <p className="mb-0 text-green-700 dark:text-green-300">
              Use the Visualisation tool to present infrastructure designs to stakeholders. The visual 
              representation makes it much easier to communicate complex architectures and get approval 
              before deployment.
            </p>
          </div>
        </div>
      </motion.div>

      <ImageModal
        isOpen={selectedImage !== null}
        src={selectedImage || ''}
        onClose={() => setSelectedImage(null)}
        alt="Screenshot"

      />
    </div>
  );
};

export default Visualisation;
